import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarInput, Stars } from "@/components/Rating";
import { Star, Loader2, ShieldCheck, Lock, Clock } from "lucide-react";
import { toast } from "sonner";

export function ReviewPanel({ collabId, myRole, otherName }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => api.get(`/collaborations/${collabId}/reviews`).then((r) => {
    setInfo(r.data);
    if (r.data.my_review) { setRating(r.data.my_review.rating); setComment(r.data.my_review.comment || ""); }
  }).finally(() => setLoading(false)), [collabId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/collaborations/${collabId}/reviews`, { rating, comment });
      toast.success("Thanks for your review!");
      setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit review"); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  if (!info || !info.opened_at) return null; // window not open

  const subjectLabel = myRole === "business" ? "creator" : "business";
  const closesPretty = info.closes_at ? new Date(info.closes_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
  const published = info.published || [];
  const mine = info.my_review;
  const showForm = info.window_open && (!mine || editing) && !(mine && mine.locked);

  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="review-panel">
      <div className="flex items-center gap-2">
        <Star className="h-5 w-5 text-amber-500" />
        <h2 className="font-display text-lg font-bold text-zinc-900">Reviews</h2>
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Verified collaboration</span>
      </div>
      <p className="mt-1 text-xs text-zinc-500">{info.double_blind_note}{info.closes_at && ` Closes ${closesPretty}.`}</p>

      {/* Published (double-blind released) */}
      {published.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="reviews-published">
          {published.map((r) => (
            <div key={r.id} className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{r.reviewer_role === myRole ? "Your review" : `${otherName || "The other party"}'s review`}</span>
                <Stars value={r.rating} size={14} />
              </div>
              {r.comment && <p className="mt-2 text-sm text-zinc-700">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Waiting state: I submitted, not yet published */}
      {mine && published.length === 0 && !editing && (
        <div className="mt-4 rounded-2xl bg-zinc-50 p-4" data-testid="review-waiting">
          <p className="flex items-center gap-2 text-sm font-medium text-zinc-900"><Clock className="h-4 w-4 text-zinc-400" /> Your review is submitted{mine.locked ? " and locked." : "."}</p>
          <div className="mt-2"><Stars value={mine.rating} size={16} /></div>
          {mine.comment && <p className="mt-1 text-sm text-zinc-600">{mine.comment}</p>}
          <p className="mt-2 text-xs text-zinc-500">{info.other_submitted ? "Both reviews will publish shortly." : `It stays hidden until ${otherName || "the other party"} submits or the window closes on ${closesPretty}.`}</p>
          {!mine.locked && info.window_open && <Button size="sm" variant="outline" data-testid="review-edit" onClick={() => setEditing(true)} className="mt-3 rounded-full">Edit my review</Button>}
        </div>
      )}

      {/* Locked, window closed, nothing published for me */}
      {!mine && !info.window_open && published.length === 0 && (
        <p className="mt-4 flex items-center gap-2 text-sm text-zinc-500" data-testid="review-closed"><Lock className="h-4 w-4" /> The review window has closed.</p>
      )}

      {/* Form */}
      {showForm && (
        <div className="mt-4" data-testid="review-form">
          <p className="text-sm text-zinc-600">Rate your experience with the {subjectLabel}. Your review appears on their public profile once published (see timing above).</p>
          <div className="mt-3"><StarInput value={rating} onChange={setRating} /></div>
          <Textarea data-testid="review-comment" rows={3} value={comment} maxLength={1000}
            onChange={(e) => setComment(e.target.value)} className="mt-3" placeholder={`How was working with the ${subjectLabel}? (optional)`} />
          <p className="mt-1 text-right text-xs text-zinc-400">{comment.length}/1000</p>
          <div className="mt-2 flex gap-2">
            <Button data-testid="review-submit" disabled={busy} onClick={submit} className="rounded-full bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mine ? "Update review" : "Submit review")}</Button>
            {mine ? <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
                  : <Button variant="ghost" data-testid="review-notnow" onClick={() => toast("No problem — you can review any time before the window closes.")} className="rounded-full text-zinc-500">Not now</Button>}
          </div>
        </div>
      )}
    </div>
  );
}
